import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';

// Types
import { Account } from '@/types/planning';
import { CellContext, Row } from '@tanstack/react-table';
import { ReduxState } from '@/redux/modules/types';
import Locale from '@/types/locale';

// Utils
import { formatLocalizedString, formatMessage, FormattedMessage } from '@/utils/i18n';
import { renderCurrencyCell, renderPercentageCell, renderValueCell } from '@/utils/data-table';
import { PrimaryButton } from '@/views/components/buttons';
import { PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import User from '@/types/user';
import Nullable from '@/types/nullable';
import { Pill } from '@capintel/component-lib/pill';

type Props = {
  i18nBasePath: string;
  isImportPage?: boolean;
  buttonActions?: {
    onCreatePortfolio?: (accountId: string) => void;
    onSelectPlanAccount?: (planAccountId: string) => void;
  };
  user?: Nullable<User>;
  selectedPlanAccountId?: string;
};

export enum AccountColumns {
  NAME = 'name',
  OWNER = 'owner',
  LINKED_PORTFOLIO = 'linkedPortfolio',
  RISK_PROFILE = 'riskProfile',
  TARGET_RATE_OF_RETURN = 'targetRateOfReturn',
  TOTAL_VALUE = 'totalValue',
  ACTION_CREATE_PORTFOLIO = 'actionCreatePortfolio',
  ACTION_SELECT_PLAN_ACCOUNT = 'actionSelectPlanAccount',
}

export const useAccountColumnsDefinition = ({ i18nBasePath, isImportPage, buttonActions, selectedPlanAccountId }: Props) => {
  const locale = useSelector((state: ReduxState) => state.intl.locale);
  const user = useSelector((state: ReduxState) => state.auth.user);
  const getColumns = useMemo(
    () => getColumnsDefinition({ i18nBasePath, locale, buttonActions, user, selectedPlanAccountId }),
    [i18nBasePath, buttonActions, locale, user, selectedPlanAccountId],
  );
  return {
    getColumns,
    isRowDisabled,
    isRowForcefullySelected: isRowForcefullySelected(!!isImportPage),
    getCheckboxTooltip: getCheckboxTooltip(i18nBasePath, !!isImportPage),
  };
};

export const isRowForcefullySelected = (isImportPage: boolean) => (row: Row<Account>) => {
  if (!isImportPage) {
    return false;
  }
  return row.original.imported && !!row.original.riskProfile;
};

export const isRowDisabled = (row: Row<Account>) => !row.original.riskProfile;

export const getCheckboxTooltip = (i18nBasePath: string, isImportPage: boolean) => (row: Row<Account>) => {
  if (isRowDisabled(row)) {
    return { message: formatMessage(`${i18nBasePath}.Table.DisabledTooltip`), title: formatMessage(`${i18nBasePath}.Table.DisabledTooltipTitle`) };
  }
  return isRowForcefullySelected(isImportPage)(row) ? { message: formatMessage(`${i18nBasePath}.Table.ForcefullySelectedTooltip`) } : { message: '' };
};

export const getColumnsDefinition = ({ i18nBasePath, buttonActions, locale, user, selectedPlanAccountId }: Props & { locale: string }) => {
  const renderRiskProfilePill = (cell: CellContext<Account, any>) => {
    const pillLabel = cell.getValue();
    const localized = formatLocalizedString(pillLabel, locale as Locale);
    return localized ? (
      <span className="pill">
        <Pill size="small">{localized}</Pill>
      </span>
    ) : null;
  };

  const renderCreatePortfolioCell = (cell: CellContext<Account, any>) => {
    const accountHasRiskProfile = cell.row.original.riskProfile;
    const userCanCreatePortfolio = userHasPermissionEnabled(user, PERMISSIONS.PORTFOLIOS_CREATE);
    const cantCreatePortfolio = !buttonActions?.onCreatePortfolio || !accountHasRiskProfile || !userCanCreatePortfolio;

    if (cantCreatePortfolio) {
      return null;
    }

    const onClick = () => buttonActions.onCreatePortfolio!(cell.row.original._id);

    return (
      <PrimaryButton className="primary-v2 btn-create-portfolio" onClick={onClick} compact>
        <FormattedMessage id={`${i18nBasePath}.Table.CreatePortfolioButton`} />
      </PrimaryButton>
    );
  };

  const renderSelectPlanAccountCell = (cell: CellContext<Account, any>) => {
    const accountHasRiskProfile = cell.row.original.riskProfile;
    const cantSelectPlanAccount = !buttonActions?.onSelectPlanAccount || !accountHasRiskProfile;
    if (cantSelectPlanAccount) {
      return null;
    }

    const onClick = () => buttonActions.onSelectPlanAccount!(cell.row.original._id);
    return !selectedPlanAccountId || selectedPlanAccountId !== cell.row.original._id ? (
      <PrimaryButton className="primary-v2 btn-select-plan-account" onClick={onClick} compact>
        <FormattedMessage id={`${i18nBasePath}.AddToPortfolio`} />
      </PrimaryButton>
    ) : (
      <span className="plan-account-added">
        <FormattedMessage id={`${i18nBasePath}.Added`} />
      </span>
    );
  };

  const getHeader = (accessor: AccountColumns) => {
    const capitalizedAccessor = accessor.charAt(0).toUpperCase() + accessor.slice(1);
    return () => <span>{formatMessage(`${i18nBasePath}.Table.Headers.${capitalizedAccessor}`)}</span>;
  };

  const renderNameCell = (cell: CellContext<Account, any>) => {
    const account = cell.row.original;
    return (
      <span id={`account-name-${account._id}`} data-private data-dd-privacy="mask">
        {cell.getValue()}
      </span>
    );
  };

  const getColumns = (columnIds?: AccountColumns[]) =>
    [
      {
        id: AccountColumns.NAME,
        accessorKey: 'name',
        cell: renderNameCell,
        header: getHeader(AccountColumns.NAME),
      },
      {
        id: AccountColumns.OWNER,
        accessorKey: 'owner',
        cell: renderValueCell,
        header: getHeader(AccountColumns.OWNER),
      },
      {
        id: AccountColumns.LINKED_PORTFOLIO,
        accessorKey: 'linkedPortfolioCount',
        cell: renderValueCell,
        header: getHeader(AccountColumns.LINKED_PORTFOLIO),
        size: 140,
      },
      {
        id: AccountColumns.TOTAL_VALUE,
        accessorKey: 'totalValue',
        cell: renderCurrencyCell,
        header: getHeader(AccountColumns.TOTAL_VALUE),
      },
      {
        id: AccountColumns.RISK_PROFILE,
        accessorFn: (account: Account) => (account.riskProfile?.title ? account.riskProfile.title : ''),
        cell: renderRiskProfilePill,
        header: getHeader(AccountColumns.RISK_PROFILE),
      },
      {
        id: AccountColumns.TARGET_RATE_OF_RETURN,
        accessorKey: 'targetRateOfReturn',
        cell: renderPercentageCell,
        header: getHeader(AccountColumns.TARGET_RATE_OF_RETURN),
      },
      {
        accessorKey: AccountColumns.ACTION_CREATE_PORTFOLIO,
        enableSorting: false,
        cell: renderCreatePortfolioCell,
        header: '',
        id: AccountColumns.ACTION_CREATE_PORTFOLIO,
        size: 140,
      },
      {
        accessorKey: AccountColumns.ACTION_SELECT_PLAN_ACCOUNT,
        enableSorting: false,
        cell: renderSelectPlanAccountCell,
        header: '',
        id: AccountColumns.ACTION_SELECT_PLAN_ACCOUNT,
      },
    ].filter(column => (columnIds ? columnIds.includes(column.id as AccountColumns) : true));

  return getColumns;
};
